import React, { useState } from 'react';
import './SetupPage.css';
import { useSetup } from '../context/SetupContext';
import { useVerbatim } from '../context/VerbatimContext';
import { useProject } from '../context/ProjectContext';
import { base64ToImage, renderVerbatimTranslation } from '../utils/VerbatimUtils';
import PrimaryButton from '../components/buttons/PrimaryButton';
import IconButton from '../components/buttons/IconButton';
import StatusBar from '../components/StatusBar';
import AddLabelModal from '../modals/AddLabelModal';
import SetupManagement from '../components/setup/SetupManagement';


const SetupPage = () => {
  const {
    labels,
    labelTypes,
    setLabels,
    setLabelTypes,
    handleEditLabelType,
    handleEditLabel,
    handleMandatoryChange,
    handleSentimentChange,
    handleMultiValueChange,
    handleLockableChange,
    addLabel,
    deleteLabelType,
    deleteLabel,
    updateKeywords,
    handleFileSelect,
  } = useSetup();

  // Inside SetupPage component, use context values and setters directly
  const {
    enableAutosave,
    setEnableAutosave,
    autosaveInterval,
    setAutosaveInterval,
  } = useProject();

  // State management for various UI interactions
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [editingLabel, setEditingLabel] = useState('');
  const [editingLabelName, setEditingLabelName] = useState('');
  const [editingType, setEditingType] = useState('');
  const [editingLabelType, setEditingLabelType] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [editingKeywordLabel, setEditingKeywordLabel] = useState('');
  // Local state for managing the autosave settings form inputs
  const [isAutosaveEnabled, setIsAutosaveEnabled] = useState(enableAutosave);
  const [autosaveTimeInterval, setAutosaveTimeInterval] = useState(autosaveInterval);
  const [isAddLabelModalOpen, setIsAddLabelModalOpen] = useState(false);
  const existingLabelTypes = Object.keys(labelTypes);
  const [statusMessage, setStatusMessage] = useState('');

  const [latestFile, setLatestFile] = useState(null);
  const [otherFiles, setOtherFiles] = useState([]);
  const [allVerbatims, setAllVerbatims] = useState([]);
  const [latestVerbatims, setLatestVerbatims] = useState([]);
  const [showMissingOnly, setShowMissingOnly] = useState(true);
  const [mergedData, setMergedData] = useState(null);
  const [selectedVerbatims, setSelectedVerbatims] = useState([]);
  const [comparisonComplete, setComparisonComplete] = useState(false);

  const openAddLabelModal = () => setIsAddLabelModalOpen(true);
  const closeAddLabelModal = () => setIsAddLabelModalOpen(false);

  const handleApplyAutosaveSettings = () => {
    setEnableAutosave(isAutosaveEnabled);
    setAutosaveInterval(autosaveTimeInterval);
    alert("New autosave settings have been applied successfully.");
  };

  // Handlers for input changes
  const handleAutosaveToggle = (e) => setIsAutosaveEnabled(e.target.checked);
  const handleAutosaveIntervalChange = (e) => setAutosaveTimeInterval(Number(e.target.value));


  // Track the active tab in the UI
  const [activeTab, setActiveTab] = useState('labelTypes');

  const handleLoadExcel = () => {
    const fileInput = document.getElementById('setup-xlsx-file-input');
    if (fileInput) {
      // Clear any previous value to ensure the 'change' event triggers even for the same file
      fileInput.value = null;
  
      fileInput.addEventListener('change', (event) => {
        const files = event.target.files; // Get the selected files
        if (files && files.length > 0) {
          handleFileSelect(event); // Call your file select handler
          setActiveTab('manageLabels'); // Update tab
          setStatusMessage("Labels successfully loaded"); // Update status message
        }
      });
  
      fileInput.click();
    } else {
      console.error('File input element not found.');
    }
  };
  
  // Filter labels based on search query and type filters
  // This function takes the current labels and applies both the search query and type filters.
  // It returns a filtered list of labels that match the criteria, which can then be used for rendering.
  const filteredLabels = Object.entries(labels || {})
    .flatMap(([labelType, labelArray]) =>
      Array.isArray(labelArray)
        ? labelArray
          .filter(({ name }) => name?.toLowerCase().includes(searchQuery.toLowerCase()))
          .map((labelObj) => ({ labelType, ...labelObj })) // Use objects for easier de-duplication
        : []
    )
    .filter(({ labelType }) => (filterType ? labelType === filterType : true))
    .reduce((uniqueLabels, labelObj) => {
      const exists = uniqueLabels.some(
        (item) => item.labelType === labelObj.labelType && item.name === labelObj.name
      );

      // Only add the label if it doesn't already exist in the uniqueLabels array
      if (!exists) uniqueLabels.push(labelObj);

      return uniqueLabels;
    }, []);

  const handleInputChange = (e) => {
    const textarea = e.target;
    textarea.style.height = "auto"; // Reset height
    textarea.style.height = `${textarea.scrollHeight}px`; // Adjust height to fit content
    setNewKeyword(textarea.value);
  };

  /* UTILITY FUNCTIONS FOR THE RESTORE FUNCTIONALITY
  */
 
  const handleLatestFileUpload = async (event) => {
    const file = event.target.files[0];
    setLatestFile(file);
    const latestData = await readJsonFile(file);
    setLatestVerbatims(latestData.verbatims || []);
    setMergedData({
      verbatims: latestData.verbatims || [],
      notes: latestData.notes || {},
      slides: latestData.slides || [],
      labels: latestData.labels || {},
      labelTypes: latestData.labelTypes || {}
    });
    setComparisonComplete(false); // Reset comparison complete state
  };

  const handleOtherFilesUpload = (event) => {
    setOtherFiles([...event.target.files]);
  };

  const handleLoadAndDisplayVerbatims = async () => {
    if (!latestFile || otherFiles.length === 0) {
      alert("Please upload the latest file and at least one other file.");
      return;
    }

    const allVerbatimData = [...latestVerbatims];
    const allLabelsData = mergedData?.labels || {};
    const allLabelTypesData = { ...mergedData?.labelTypes } || {}; // Initialize as an object
    const allNotesData = [...mergedData?.notes] || [];
    const allSlidesData = [...mergedData?.slides] || [];

    for (const file of otherFiles) {
      const otherData = await readJsonFile(file);

      // Merge unique verbatims only
      if (otherData.verbatims) {
        otherData.verbatims.forEach((verbatim) => {
          const isDuplicate = allVerbatimData.some((v) => {
            let isIdentical = false;

            if (typeof verbatim.verbatim === "string") {
              if (v.verbatim === verbatim.verbatim) {
                isIdentical = true;
              }
            }

            if (Array.isArray(verbatim.verbatim)) {
              if (v.verbatim === verbatim.verbatim[0]?.content) {
                isIdentical = true;
              }
            }

            return v.id === verbatim.id && isIdentical;
          });
          
          if (!isDuplicate) {
            allVerbatimData.push(verbatim);
          }
        });
      }

      // Merge labels
      if (otherData.labels) {
        Object.entries(otherData.labels).forEach(([key, value]) => {
          if (!allLabelsData[key]) allLabelsData[key] = value;
        });
      }

      // Merge labelTypes
      if (otherData.labelTypes) {
        Object.entries(otherData.labelTypes).forEach(([key, value]) => {
          if (!allLabelTypesData[key]) allLabelTypesData[key] = value;
        });
      }


      // Merge notes
      if (otherData.notes) {
        Object.entries(otherData.notes).forEach(([key, value]) => {
          if (!allNotesData[key]) allNotesData[key] = value;
        });
      }

      // Merge slides only if empty in the latest
      if (otherData.slides && allSlidesData.length === 0) {
        allSlidesData.push(...otherData.slides);
      }
    }

    setAllVerbatims(allVerbatimData);
    setMergedData((prevData) => ({
      ...prevData,
      labels: allLabelsData,
      labelTypes: allLabelTypesData,
      notes: allNotesData,
      slides: allSlidesData,
    }));

  };

  const handleCompareAndFill = () => {
    const filledVerbatims = [...latestVerbatims];
    allVerbatims.forEach((verbatim) => {
      const isDuplicate = latestVerbatims.some((v) => {
        let isIdentical = false;

        if (typeof verbatim.verbatim === "string") {
          if (v.verbatim === verbatim.verbatim) {
            isIdentical = true;
          }
        }

        if (Array.isArray(verbatim.verbatim)) {
          if (v.verbatim === verbatim.verbatim[0]?.content) {
            isIdentical = true;
          }
        }

        return v.id === verbatim.id && isIdentical;
      });

      if (selectedVerbatims.includes(verbatim.id) && !isDuplicate) {
        filledVerbatims.push(verbatim);
      }
    });

    setMergedData((prevData) => ({
      ...prevData,
      verbatims: filledVerbatims
    }));
    setComparisonComplete(true); // Mark comparison as complete
  };

  const readJsonFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target.result);
          resolve(json);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };

  const handleDownloadMergedFile = () => {
    if (!mergedData) return;

    const json = JSON.stringify(mergedData, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'merged-latest-file.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleVerbatimSelection = (id) => {
    setSelectedVerbatims((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((selectedId) => selectedId !== id)
        : [...prevSelected, id]
    );
  };

  const handleSelectAll = () => {
    const allIds = filteredVerbatims.map((verbatim) => verbatim.id);
    setSelectedVerbatims(
      selectedVerbatims.length === allIds.length ? [] : allIds
    );
  };
  

  const filteredVerbatims = showMissingOnly
    ? allVerbatims.filter(
      (verbatim) => !latestVerbatims.some((v) => {
        let isIdentical = false;

        if (typeof verbatim.verbatim === "string") {
          if (v.verbatim === verbatim.verbatim) {
            isIdentical = true;
          }
        }

        if (Array.isArray(verbatim.verbatim)) {
          if (v.verbatim === verbatim.verbatim[0]?.content) {
            isIdentical = true;
          }
        }

        return v.id === verbatim.id && isIdentical;
      })
    )
    : allVerbatims;


  return (
    <div className="page-container">
      <div className="setup-container">
        {/* Tab header */}
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'labelTypes' ? 'active' : ''}`}
            onClick={() => setActiveTab('labelTypes')}
          >
            <i className="fa-solid fa-list setup-tab-icon"></i>
            Manage Label Types
          </button>
          <button
            className={`tab-button ${activeTab === 'manageLabels' ? 'active' : ''}`}
            onClick={() => setActiveTab('manageLabels')}
          >
            <i className="fa-solid fa-tag setup-tab-icon"></i>
            Manage Labels
          </button>
          <button
            className={`tab-button ${activeTab === 'fileActions' ? 'active' : ''}`}
            onClick={() => setActiveTab('fileActions')}
          >
            <i className="fa-solid fa-file-import setup-tab-icon"></i>
            Import / Export Setup
          </button>
          <button
            className={`tab-button ${activeTab === 'autosaveSettings' ? 'active' : ''}`}
            onClick={() => setActiveTab('autosaveSettings')}
          >
            <i className="fa-solid fa-save setup-tab-icon"></i>
            Autosave Settings
          </button>
          <button
            className={`tab-button ${activeTab === 'restoreSave' ? 'active' : ''}`}
            onClick={() => setActiveTab('restoreSave')}
          >
            <i className="fa-solid fa-wrench setup-tab-icon"></i>
            Restore Save
          </button>
        </div>

        {/* Tab content */}
        <div className="tabs-content">
          {activeTab === 'labelTypes' && (
            <div className="table-container">
              {/* Label Types Tab */}
              <table id="label-types-table">
                <thead>
                  <tr>
                    <th className="setup-table-headers">Type</th>
                    <th className="setup-table-headers">Number of Labels</th>
                    <th className="setup-table-headers">
                      Mandatory
                      <i className="fa-solid fa-exclamation-triangle tab-icon" title="Mandatory"></i>
                    </th>
                    <th className="setup-table-headers">
                      Sentiment-enabled
                      <i className="fa-solid fa-smile tab-icon" title="Sentiment Enabled"></i>
                    </th>
                    <th className="setup-table-headers">
                      Multi-value
                      <i className="fa-solid fa-tags tab-icon" title="Multiple Values Possible"></i>
                    </th>
                    {/*<th className="setup-table-headers">
                      Lockable
                      <i className="fa-solid fa-lock tab-icon" title="Lock Possible"></i>
                    </th>*/}
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(labelTypes).length > 0 ? (
                    Object.entries(labelTypes).map(([type, typeData]) => {
                      const finalTypeData = {
                        mandatory: false,
                        sentimentEnabled: false,
                        multiValue: false,
                        lockable: false,
                        ...typeData,
                      };
                      const labelCount = labels[type]?.length || 0;
                      const isPredefined = ['Gender', 'Age', 'Sentiment', 'Sources'].includes(type);

                      return (
                        <tr key={type}>
                          <td className="strong-text">
                            {editingLabel === type ? (
                              <>
                                {/* Input field to edit the label type name */}
                                <input
                                  type="text"
                                  value={editingLabelName}
                                  onChange={(e) => setEditingLabelName(e.target.value)} // Update input field
                                />
                                {/* Button to confirm the name change */}
                                <IconButton
                                  iconClass="fa fa-check"
                                  onClick={() => {
                                    handleEditLabelType(type, editingLabelName); // Handle the name change
                                    setEditingLabel(''); // Clear editing mode
                                    setEditingLabelName(''); // Clear input
                                  }}
                                />
                              </>
                            ) : (
                              <>
                                {/* Display the current label type name */}
                                {type}
                                {/* Only render edit/delete buttons if type is not Gender, Age, or Sentiment */}
                                {!['Gender', 'Age', 'Sentiment', 'Sources'].includes(type) && (
                                  <>
                                    {/* Button to start editing the label type */}
                                    <IconButton
                                      iconClass="fa fa-edit"
                                      onClick={() => {
                                        setEditingLabel(type); // Set the current type as being edited
                                        setEditingLabelName(type); // Pre-fill the input with the current type name
                                      }}
                                    />
                                    {/* Button to delete the label type */}
                                    <IconButton
                                      iconClass="fa fa-trash" // Trash icon for delete
                                      onClick={() => {
                                        if (window.confirm(`Are you sure you want to delete the label type "${type}" and all related labels?`)) {
                                          deleteLabelType(type); // Call the delete function to remove the label type and all related labels
                                        }
                                      }}
                                    />
                                  </>
                                )}
                              </>
                            )}
                          </td>
                          <td>{labelCount}</td>
                          <td>
                            <select
                              className="label-type-check"
                              value={finalTypeData.mandatory.toString()}
                              onChange={(e) => handleMandatoryChange(type, e.target.value)}
                              disabled={isPredefined}
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          </td>
                          <td>
                            <select
                              className="label-type-check"
                              value={finalTypeData.sentimentEnabled.toString()}
                              onChange={(e) => handleSentimentChange(type, e.target.value)}
                              disabled={isPredefined}
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          </td>
                          <td>
                            <select
                              className="label-type-check"
                              value={finalTypeData.multiValue.toString()}
                              onChange={(e) => handleMultiValueChange(type, e.target.value)}
                              disabled={isPredefined}
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          </td>
                          {/*<td>
                            <select
                              className="label-type-check"
                              value={finalTypeData.lockable.toString()}
                              onChange={(e) => handleLockableChange(type, e.target.value)}
                              disabled={isPredefined}
                            >
                              <option value="true">true</option>
                              <option value="false">false</option>
                            </select>
                          </td>*/}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '1em' }}>
                        No label types found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'manageLabels' && (
            <div>
              {/* Manage Labels Tab */}
              <div className="actions-container">
                <div className="search-filter-section">
                  <div className="search-section">
                    <div className="search-container">
                      <i className="fa fa-search search-icon"></i>
                      <input
                        type="text"
                        id="search-query"
                        placeholder="Search for a label by name"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="filter-section">
                    <select id="filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                      <option value="">All</option>
                      {Object.keys(labelTypes).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="label-creation">
                  <PrimaryButton onClick={openAddLabelModal}>
                    + Add New Label
                  </PrimaryButton>
                </div>
              </div>
              <div className="table-container">
                <table id="label-keyword-table">
                  <thead>
                    <tr>
                      <th className="setup-table-headers">Label</th>
                      <th className="setup-table-headers">Type</th>
                      <th className="setup-table-headers">Keywords</th>
                      <th className="setup-table-headers">Explanation</th> {/* New Explanation Column */}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLabels.length > 0 ? (
                      filteredLabels.map(({ labelType, name, keywords, explanation }) => {
                        const label = name;
                        const data = {
                          type: labelType,
                          keywords: keywords || [], // Ensure keywords is always an array
                          explanation: explanation || '', // Ensure explanation is defined
                        };

                        return (
                          <tr key={label}>
                            <td className="strong-text">
                              {editingLabel === label ? (
                                <>
                                  <input
                                    type="text"
                                    value={editingLabelName}
                                    onChange={(e) => setEditingLabelName(e.target.value)}
                                  />
                                  <IconButton
                                    iconClass="fa fa-check"
                                    onClick={() => {
                                      handleEditLabel(label, data.type, editingLabelName, data.type);
                                      setEditingLabel('');
                                      setEditingLabelName(''); // Clear input
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  {label}
                                  {!['Gender', 'Age', 'Sentiment', 'Sources'].includes(data.type) && (
                                    <>
                                      <IconButton
                                        iconClass="fa fa-pencil"
                                        onClick={() => {
                                          setEditingLabel(label);
                                          setEditingLabelName(label); // Set the initial value for editing label name
                                        }}
                                      />
                                      <IconButton
                                        iconClass="fa fa-trash"
                                        onClick={() => deleteLabel(label, labelType)} // Pass both label and labelType
                                      />
                                    </>
                                  )}
                                </>
                              )}
                            </td>

                            <td>
                              {editingType === label ? (
                                <>
                                  <input
                                    type="text"
                                    value={editingLabelType}
                                    onChange={(e) => setEditingLabelType(e.target.value)}
                                  />
                                  <IconButton
                                    iconClass="fa fa-check"
                                    onClick={() => {
                                      handleEditLabel(label, data.type, label, editingLabelType);
                                      setEditingType('');
                                    }}
                                  />
                                </>
                              ) : (
                                <>
                                  {data.type}
                                  {!['Gender', 'Age', 'Sentiment', 'Sources'].includes(data.type) && (
                                    <IconButton
                                      iconClass="fa fa-pencil"
                                      onClick={() => {
                                        setEditingType(label);
                                        setEditingLabelType(data.type); // Set the initial value for editing label type
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </td>

                            <td>
                              {editingKeywordLabel === label ? (
                                <div className="edit-keyword-container">
                                  <textarea
                                    className="keyword-input"
                                    value={newKeyword}
                                    placeholder="Enter keywords separated by commas or semicolons"
                                    onChange={handleInputChange}
                                  ></textarea>
                                  <IconButton
                                    iconClass="fa fa-check"
                                    onClick={() => {
                                      const cleanedKeywords = newKeyword
                                        .split(/[,;]/) // Split by commas or semicolons
                                        .map((kw) => kw.trim()) // Remove leading/trailing spaces
                                        .filter((kw) => kw); // Filter out empty strings

                                      updateKeywords(labelType, label, cleanedKeywords);
                                      setEditingKeywordLabel(''); // Close the edit mode
                                      setNewKeyword(''); // Clear the input
                                    }}
                                  />
                                </div>
                              ) : (
                                <div>
                                  {data.keywords.join('; ')}
                                  {!['Gender', 'Age', 'Sentiment'].includes(data.type) && (
                                    <IconButton
                                      iconClass="fa fa-pencil"
                                      onClick={() => {
                                        setEditingKeywordLabel(label);
                                        setNewKeyword(data.keywords.join('; ')); // Set current keywords as initial value
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                            </td>

                            {/* New Explanation Column */}
                            <td>
                              {data.explanation}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '1em' }}>
                          No labels found. Add a new label to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'fileActions' && (
            <div>
              {/* File Actions Tab */}
              <div className="file-actions">
                  <SetupManagement 
                    handleLoadExcel={handleLoadExcel} 
                    onClick={(labelTypes, labels) => {
                      setLabels(labels);
                      setLabelTypes(labelTypes);
                      setActiveTab('manageLabels');
                      setStatusMessage("Labels successfully loaded");
                    }}
                  /> 
              </div>
            </div>
          )}
          {activeTab === 'autosaveSettings' && (
            <div className="autosave-settings">
              <label>
                <input
                  type="checkbox"
                  checked={isAutosaveEnabled}
                  onChange={handleAutosaveToggle}
                />
                Enable Autosave
              </label>
              <div className="autosave-interval">
                <label>Autosave Interval (minutes):</label>
                <input
                  type="number"
                  min="1"
                  value={autosaveTimeInterval}
                  onChange={handleAutosaveIntervalChange}
                />
              </div>
              <div>
                <p>
                  If you're having trouble downloading files because your browser is blocking them after too many downloads, here’s how you can resolve the issue for common browsers:
                </p>
                <ul>
                  <li>
                    <strong>Google Chrome:</strong> Chrome allows you to manage download permissions globally. Go to <em>Settings</em> - <em>Privacy and security</em> - <em>Site settings</em> - <em>Additional permissions</em> - <em>Automatic downloads</em>. Make sure downloads are allowed. If a site is listed under blocked sites, remove it to enable downloads.
                  </li>
                  <li>
                    <strong>Mozilla Firefox:</strong> Firefox doesn’t offer a global setting for downloads. If a download is blocked, look for a notification bar or popup at the top of the browser. Click on <em>Allow</em> or <em>Retry</em> to enable the download. You may need to do this each time the browser prompts you.
                  </li>
                  <li>
                    <strong>Opera:</strong> Like Firefox, Opera doesn’t allow global permissions for downloads. If your download is blocked, look for a warning message near the address bar or at the bottom of the browser. Select <em>Allow</em> to enable the download. You’ll need to approve downloads as they occur.
                  </li>
                </ul>
                <p>
                  These steps address common download-blocking issues. If you're downloading many files, the browser may temporarily block them as a security measure. Restarting your browser or clearing temporary restrictions can help resolve persistent issues.
                </p>
              </div>


              <PrimaryButton onClick={handleApplyAutosaveSettings}>
                Apply
              </PrimaryButton>
            </div>
          )}
          {activeTab === 'restoreSave' && (
            <div className="upload-comparison-page">
            <h2>Upload and Compare Files</h2>
            <p>
              This page allows you to upload the latest file along with previous versions of files to identify
              any missing verbatims in the latest file. The table below will show only the missing verbatims by default.
              You can toggle the checkbox to see all verbatims if needed.
            </p>
      
            <div>
              <label>Upload Latest File:</label>
              <input type="file" accept=".json" onChange={handleLatestFileUpload} />
            </div>
            <p>
              The "latest file" should contain the most recent version of the project export file you want to check.
            </p>
      
            <div>
              <label>Upload Other Files:</label>
              <input type="file" accept=".json" multiple onChange={handleOtherFilesUpload} />
            </div>
            <p>
              Upload one or more "other files" containing previous versions of the project export file.
              These files will be checked for entries that may be missing in the latest project export.
            </p>
      
            <PrimaryButton onClick={handleLoadAndDisplayVerbatims}>
              Load and Display Verbatims
            </PrimaryButton>
      
            {allVerbatims.length > 0 && (
              <div>
                <h3>Missing Verbatims in Latest File</h3>
                <p>
                  The table below shows verbatims that are missing from the latest file, based on the combination of ID and content.
                  You can review these entries and select the ones you wish to include in the merged file.
                </p>
                <label>
                  <input
                    type="checkbox"
                    checked={showMissingOnly}
                    onChange={() => setShowMissingOnly(!showMissingOnly)}
                  />
                  Show Only Missing Verbatims
                </label>
                <PrimaryButton onClick={handleSelectAll}>
                  {selectedVerbatims.length === filteredVerbatims.length ? 'Deselect All' : 'Select All'}
                </PrimaryButton>
                <table className="upload-comparison-table">
                  <thead>
                    <tr>
                      <th className="small-column">Select</th>
                      <th className="small-column">ID</th>
                      <th>Verbatim</th>
                      <th>Translation</th>
                      <th>Labels</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVerbatims.map((verbatim, index) => (
                      <tr key={index}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedVerbatims.includes(verbatim.id)}
                            onChange={() => handleVerbatimSelection(verbatim.id)}
                          />
                        </td>
                        <td>{verbatim.id || 'N/A'}</td>
                        <td>
                          {typeof verbatim.verbatim === 'string' && verbatim.verbatim || 'No text content'}
                          {Array.isArray(verbatim.verbatim) && verbatim.verbatim.map((item, index) => {
                              if (item.type === 'text' && item.content.trim() !== '') {
                                  return <span key={index}>{item.content || 'No text content'}</span>;
                              } else if (item.type === 'image' && item.content) {
                                  return (
                                      <div key={index} className="review-image-block">
                                          <img 
                                              src={base64ToImage(item.content)} 
                                              alt="Verbatim" 
                                              className="review-verbatim-image"
                                              onError={(e) => {
                                              console.error("Erreur de chargement de l'image", e);
                                              e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                                              }}
                                          />
                                      </div>
                                  );
                              }
                              return null;
                          })}
                        </td>
                        <td>{renderVerbatimTranslation(verbatim.translation)}</td>
                        <td>{JSON.stringify(verbatim.labels)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <PrimaryButton onClick={handleCompareAndFill}>
                  Merge Data
                </PrimaryButton>
              </div>
            )}
      
            {comparisonComplete && mergedData && (
              <div>
                <h3>Merging Complete</h3>
                <p>
                  The merging is complete. You can now download the merged file,
                  which includes all selected entries from the previous versions not found in the latest file.
                </p>
                <PrimaryButton onClick={handleDownloadMergedFile}>Download Merged File</PrimaryButton>
              </div>
            )}
          </div>
          )}

        </div>
      </div>
      <AddLabelModal
        isOpen={isAddLabelModalOpen}
        onClose={closeAddLabelModal}
        onAddLabel={(newLabel, newLabelType, keywords, explanation) => {
          addLabel(newLabel, newLabelType, keywords, explanation);
          setIsAddLabelModalOpen(false);
        }}
        existingLabelTypes={existingLabelTypes} // Pass the array of label types
      />
      <div>
        <StatusBar message={statusMessage} type="success" />
      </div>
    </div>

  );
};

export default SetupPage;
